import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { 
  BarChart3, Users, Eye, Globe, Smartphone, Monitor, Tablet,
  TrendingUp, Clock, MousePointer, ShoppingCart, UserPlus,
  RefreshCw, ArrowUpRight, ArrowDownRight, ExternalLink
} from "lucide-react";
import { API } from "../../apiConfig";


const AnalyticsDashboard = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30");
  const [autoRefresh, setAutoRefresh] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, realtimeRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard?days=${period}`, { headers }),
        axios.get(`${API}/analytics/realtime?minutes=5`, { headers })
      ]);
      setStats(statsRes.data);
      setRealtime(realtimeRes.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  }, [period, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh realtime data every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/analytics/realtime?minutes=5`, { headers });
        setRealtime(res.data);
      } catch (e) {}
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, token]);

  const getSourceIcon = (source) => {
    const icons = {
      'google': '🔍',
      'facebook': '📘',
      'instagram': '📸',
      'twitter': '🐦',
      'linkedin': '💼',
      'direct': '🔗',
      'email': '📧'
    };
    return icons[source] || '🌐';
  };

  const getDeviceIcon = (device) => {
    switch(device) {
      case 'mobile': return <Smartphone className="h-4 w-4" />;
      case 'tablet': return <Tablet className="h-4 w-4" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const overview = stats?.overview || {};
  const trafficSources = stats?.traffic_sources || [];
  const topPages = stats?.top_pages || [];
  const devices = stats?.devices || {};
  const browsers = stats?.browsers || {};
  const conversions = stats?.conversions || {};
  const dailyTrend = stats?.daily_trend || [];

  // Calculate trend (compare last 7 days to previous 7)
  const recentDays = dailyTrend.slice(-7);
  const previousDays = dailyTrend.slice(-14, -7);
  const recentViews = recentDays.reduce((a, b) => a + (b.pageviews || 0), 0);
  const previousViews = previousDays.reduce((a, b) => a + (b.pageviews || 0), 0);
  const trend = previousViews > 0 ? ((recentViews - previousViews) / previousViews * 100).toFixed(1) : 0;
  const trendUp = trend >= 0;

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Analytics Dashboard
          </h2>
          <p className="text-muted-foreground text-sm">Track visitor behavior and traffic sources</p>
        </div>
        <div className="flex gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px] rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData} className="rounded-xl gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Real-time Visitors Card */}
      <Card className="rounded-2xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
              </div>
              <div>
                <p className="text-3xl font-bold">{realtime?.active_visitors || 0}</p>
                <p className="text-sm text-muted-foreground">Active visitors right now</p>
              </div>
            </div>
            {realtime?.visitors?.length > 0 && (
              <div className="hidden md:block text-right">
                <p className="text-sm text-muted-foreground">Currently viewing:</p>
                <p className="text-sm font-medium">{realtime.visitors[0]?.current_page}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-2xl">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Eye className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{overview.total_pageviews?.toLocaleString() || 0}</p>
                <p className="text-sm text-muted-foreground">Page Views</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{overview.unique_visitors?.toLocaleString() || 0}</p>
                <p className="text-sm text-muted-foreground">Unique Visitors</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <MousePointer className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{overview.avg_pages_per_visitor || 0}</p>
                <p className="text-sm text-muted-foreground">Pages / Visitor</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-xl ${trendUp ? 'bg-green-100' : 'bg-red-100'} flex items-center justify-center`}>
                {trendUp ? <ArrowUpRight className="h-5 w-5 text-green-600" /> : <ArrowDownRight className="h-5 w-5 text-red-600" />}
              </div>
              <div>
                <p className="text-2xl font-bold">{trendUp ? '+' : ''}{trend}%</p>
                <p className="text-sm text-muted-foreground">vs Last Week</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-xl border-dashed">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-green-600" />
              <span className="text-sm text-muted-foreground">Purchases</span>
            </div>
            <p className="text-xl font-bold mt-1">{conversions.purchase || 0}</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-dashed">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-muted-foreground">Add to Cart</span>
            </div>
            <p className="text-xl font-bold mt-1">{conversions.add_to_cart || 0}</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-dashed">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-muted-foreground">Sign Ups</span>
            </div>
            <p className="text-xl font-bold mt-1">{conversions.signup || 0}</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-dashed">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <MousePointer className="h-4 w-4 text-purple-600" />
              <span className="text-sm text-muted-foreground">Checkout Started</span>
            </div>
            <p className="text-xl font-bold mt-1">{conversions.checkout_start || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Traffic Sources */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Traffic Sources
            </CardTitle>
            <CardDescription>Where your visitors come from</CardDescription>
          </CardHeader>
          <CardContent>
            {trafficSources.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4">No traffic data yet</p>
            ) : (
              <div className="space-y-3">
                {trafficSources.map((source, i) => {
                  const maxVisits = trafficSources[0]?.visits || 1;
                  const percentage = (source.visits / maxVisits * 100).toFixed(0);
                  return (
                    <div key={i} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-sm">
                          <span>{getSourceIcon(source.source)}</span>
                          <span className="capitalize">{source.source}</span>
                        </span>
                        <span className="text-sm font-medium">{source.visits}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Pages */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              Top Pages
            </CardTitle>
            <CardDescription>Most visited pages</CardDescription>
          </CardHeader>
          <CardContent>
            {topPages.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4">No page data yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Page</TableHead>
                    <TableHead className="text-right">Views</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topPages.slice(0, 8).map((page, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium text-sm truncate max-w-[200px]">
                        {page.page || '/'}
                      </TableCell>
                      <TableCell className="text-right">{page.views}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Devices */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Devices
            </CardTitle>
            <CardDescription>Visitor device breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(devices).map(([device, count]) => (
                <div key={device} className="text-center p-4 bg-muted/30 rounded-xl">
                  <div className="flex justify-center mb-2">
                    {getDeviceIcon(device)}
                  </div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs text-muted-foreground capitalize">{device}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Browsers */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Browsers
            </CardTitle>
            <CardDescription>Visitor browser breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(browsers).map(([browser, count]) => {
                const total = Object.values(browsers).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? (count / total * 100).toFixed(0) : 0;
                return (
                  <div key={browser} className="flex items-center justify-between">
                    <span className="text-sm">{browser}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary/60 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground w-12 text-right">{percentage}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Trend Chart (Simple) */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Daily Trend
          </CardTitle>
          <CardDescription>Page views over the last {period} days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-end gap-1">
            {dailyTrend.slice(-30).map((day, i) => {
              const maxViews = Math.max(...dailyTrend.map(d => d.pageviews || 0), 1);
              const height = ((day.pageviews || 0) / maxViews * 100);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div 
                    className="w-full bg-primary/80 rounded-t transition-all hover:bg-primary"
                    style={{ height: `${Math.max(height, 2)}%` }}
                    title={`${day.date}: ${day.pageviews} views`}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>{dailyTrend[0]?.date || ''}</span>
            <span>{dailyTrend[dailyTrend.length - 1]?.date || ''}</span>
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      {overview.total_pageviews === 0 && (
        <Card className="rounded-2xl border-dashed">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="font-semibold mb-2">No Analytics Data Yet</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Analytics tracking is set up and ready. Data will appear here as visitors browse your site.
                Make sure to deploy the latest version to start collecting data.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
